// ts-rest contract for client Documents — retained, User-uploaded files about a
// client (a scanned/uploaded fact-find, suitability, vulnerability or KYC
// document), modelled as a SourceUpload with retained bytes and referenced from
// the object it backs via UploadBacked (`sourceUploadIds`).
//
// Only the JSON endpoints (list / delete / extract) live in ts-rest. The
// multipart UPLOAD and the binary CONTENT stream are raw Nest routes on the
// documents controller (ts-rest is JSON-only) — see documents.controller.ts.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBody } from '../schemas/common';
import { staffActionErrorSchema } from './agent';

const c = initContract();

/** The area a document belongs to. Drives which object it backs (UploadBacked). */
export const documentAreaSchema = z.enum([
    'fact_find',
    'suitability',
    'vulnerability',
    'kyc',
    'other',
]);
export type DocumentArea = z.infer<typeof documentAreaSchema>;

/** SourceUpload lifecycle, doubling as the AI-extraction status. */
export const documentStatusSchema = z.enum(['queued', 'processing', 'completed', 'failed']);
export type DocumentStatus = z.infer<typeof documentStatusSchema>;

/** A retained document's metadata (no bytes — the content streams separately). */
export const documentSchema = z.object({
    sourceUploadId: z.string(),
    clientId: z.string().nullable(),
    area: documentAreaSchema.nullable(),
    documentType: z.string().nullable(),
    issueDate: z.string().nullable(),
    originalFilename: z.string().nullable(),
    contentType: z.string().nullable(),
    status: documentStatusSchema,
    uploadedByUserId: z.string().nullable(),
    uploadedAt: z.string().nullable(),
});
export type DocumentDto = z.infer<typeof documentSchema>;

export const documentsContract = c.router(
    {
        listDocuments: {
            method: 'GET',
            path: '/documents/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            query: z.object({ area: documentAreaSchema.optional() }),
            responses: {
                200: z.object({ documents: z.array(documentSchema) }),
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's uploaded documents (optionally filtered by area)",
        },
        deleteDocument: {
            method: 'POST',
            path: '/documents/:sourceUploadId/delete',
            pathParams: z.object({ sourceUploadId: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Soft-delete a document (audited)',
        },
        extractDocument: {
            method: 'POST',
            path: '/documents/:sourceUploadId/extract',
            pathParams: z.object({ sourceUploadId: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), jobId: z.string().optional() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Kick off (or re-run) AI extraction of a fact-find / suitability document',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
